// The job's estimate on ui-web's EstimateBuilder (DEC-019, Blueprint): the editor's draft (job-model.ts)
// as the builder draws it. Each option a lettered column with its lines (quantity × price, the amount, a
// changed price's book price and why), its total before tax and the customer's view of it; the price
// book found as the picker's rows, at the price this customer pays; the option templates as Start from.
// Saving is still estimates.save's input from the same draft (job-model saveInput).

import type { MemberPlan } from '@flowology/domain';
import type { BuilderOption, EditorLine, PickerItem } from '@flowology/ui-web/job';
import { money } from '@flowology/ui-web/fl';
import { newId } from '../shared/ids.ts';
import { pictureSrc } from '../settings/pricebook-book-model.ts';
import type { Item } from '../settings/setup-model.ts';
import {
  bookPriceFor,
  canAddOption,
  lineCents,
  needsReason,
  optionCents,
  type Draft,
  type DraftLine,
  type DraftOption,
  type PricebookItem,
} from './job-model.ts';

/** A book item as the picker finds it (pricebook.listItems): its picture comes with it. */
export type FoundItem = PricebookItem & Partial<Pick<Item, 'art' | 'picture'>>;

export const LETTERS = 'ABCDEF';
export const TIER_WORDS: Record<DraftOption['tier'], string> = {
  '': '',
  good: 'Good',
  better: 'Better',
  best: 'Best',
};
export const TIER_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'good', label: 'Good' },
  { value: 'better', label: 'Better' },
  { value: 'best', label: 'Best' },
];
/** The picker's kinds: the book's types. */
export const KINDS = [
  { id: 'all', label: 'Everything' },
  { id: 'service', label: 'Services' },
  { id: 'material', label: 'Materials' },
  { id: 'equipment', label: 'Equipment' },
];

/** pricebook.listItems' input for the picker: what's typed, the kind, a page of twenty. */
export function findInput(query: string, kind: string): Record<string, unknown> {
  return {
    ...(query.trim() ? { query: query.trim() } : {}),
    ...(kind !== 'all' ? { type: kind } : {}),
    limit: 20,
  };
}

/**
 * A found item as a picker row, as the approved picker says it: the book price, and under it what members
 * pay ("$346.50 for members"). At a member's address that's the price the line is added at.
 */
export function pickerItemOf(item: FoundItem, plans: readonly MemberPlan[], note?: string): PickerItem {
  const p = bookPriceFor(item, plans);
  const isMember = Boolean(p.memberPriceOf && p.listPriceCents !== null);
  const members = isMember ? p.priceCents : (item.memberPriceCents ?? null);
  return {
    id: item.id,
    name: item.name,
    code: item.code,
    price: money(isMember ? p.listPriceCents! : p.priceCents),
    ...(members !== null ? { memberPrice: `${money(members)} for members` } : {}),
    picture: pictureSrc({ art: item.art, picture: item.picture ?? null }, 'drawing'),
    ...(note ? { note } : {}),
  };
}

/** What the line says under its name: the member's price, or that it was written in. */
export function lineDetail(l: DraftLine): string | undefined {
  if (l.pricebookItemId === null) return 'Written in, not from the price book';
  if (l.member) return `Member price · ${l.member.planName} · regular ${money(l.member.listPriceCents)}`;
  return undefined;
}

/** A draft line as an editor line: a book line's changed price says the book's and asks why. */
export function editorLineOf(l: DraftLine, picture?: EditorLine['picture']): EditorLine {
  const total = lineCents(l);
  const changed = l.pricebookItemId !== null && l.bookPriceCents !== null && needsReason(l);
  const detail = lineDetail(l);
  return {
    id: l.lineId,
    name: l.description.trim() || 'A line written in',
    ...(detail ? { detail } : {}),
    qty: l.quantity,
    price: l.price,
    amount: total === null ? '—' : money(total),
    ...(changed ? { wasPrice: money(l.bookPriceCents!), reason: l.reason, editReason: true } : {}),
    picture: picture ?? null,
  };
}

/** An option's lettered column, without the app's fields and links (EstimateBuild adds them). */
export function builderOptionOf(
  o: DraftOption,
  n: number,
  pictures: Readonly<Record<string, EditorLine['picture']>>,
): BuilderOption {
  const letter = LETTERS[n] ?? String(n + 1);
  const title = o.title.trim() || `Option ${n + 1}`;
  const total = money(optionCents(o));
  return {
    id: o.optionId,
    letter,
    ...(o.tier ? { tier: TIER_WORDS[o.tier] } : {}),
    title,
    ...(o.summary.trim() ? { summary: o.summary.trim() } : {}),
    lines: o.lines.map((l) => editorLineOf(l, l.pricebookItemId ? pictures[l.pricebookItemId] : undefined)),
    totals: [{ label: 'Before tax', value: total, kind: 'strong' as const }],
    shown: {
      id: o.optionId,
      name: `Option ${letter}`,
      title,
      price: total,
      points: o.lines.map((l) => l.description.trim()).filter(Boolean),
    },
  };
}

/** A copy of an option, its own ids, said as a copy; null when the estimate has its six. */
export function copyOf(d: Draft, optionId: string): DraftOption | null {
  const o = d.options.find((x) => x.optionId === optionId);
  if (!o || !canAddOption(d)) return null;
  return {
    ...o,
    optionId: newId(),
    title: `${o.title.trim() || 'Option'} (copy)`.slice(0, 120),
    lines: o.lines.map((l) => ({ ...l, lineId: newId() })),
  };
}

/** The draft with one option changed. */
export const withOption = (d: Draft, optionId: string, f: (o: DraftOption) => DraftOption): Draft => ({
  ...d,
  options: d.options.map((o) => (o.optionId === optionId ? f(o) : o)),
});

/** The draft with one line changed. */
export const withLine = (d: Draft, optionId: string, lineId: string, f: (l: DraftLine) => DraftLine): Draft =>
  withOption(d, optionId, (o) => ({ ...o, lines: o.lines.map((l) => (l.lineId === lineId ? f(l) : l)) }));
